"use client";

import { useEffect, useState } from "react";

import { ORDER_POLL_INTERVAL_MS, ORDER_POLL_TIMEOUT_MS } from "@/config/esim";
import { fetchEsimOrder } from "@/lib/actions/esim";
import type { EsimOrder } from "@/lib/types/esim";

export type DeliveryPhase = "idle" | "waiting" | "ready" | "failed" | "timeout";

/** Terminal DT One statuses that mean no eSIM is coming. */
const FAILED_STATUSES = new Set(["DECLINED", "FAILED", "REJECTED"]);

function isDelivered(order: EsimOrder): boolean {
  // A purchase is delivered when the profile exists; a top-up issues no new
  // profile, so its own transaction status is the only signal.
  return order.type === "purchase"
    ? Boolean(order.profile)
    : order.status?.message === "COMPLETED";
}

function hasFailed(order: EsimOrder): boolean {
  return FAILED_STATUSES.has(order.status?.message ?? "");
}

function phaseFor(order: EsimOrder): DeliveryPhase {
  if (isDelivered(order)) return "ready";
  if (hasFailed(order)) return "failed";
  return "waiting";
}

/**
 * Follows an order until DT One provisions it.
 *
 * `POST /purchase` returns as soon as the transaction is accepted — the ICCID
 * and activation code arrive later on a callback — so the only way to show a
 * customer their QR code is to poll. Polling stops on a terminal status or
 * after `ORDER_POLL_TIMEOUT_MS`, at which point the eSIM is not lost, it just
 * has to be picked up from the list once the callback lands.
 */
export function useEsimDelivery(submitted: EsimOrder | null): {
  order: EsimOrder | null;
  phase: DeliveryPhase;
} {
  const [order, setOrder] = useState<EsimOrder | null>(submitted);
  const [phase, setPhase] = useState<DeliveryPhase>(
    submitted ? phaseFor(submitted) : "idle",
  );

  // A new order restarts the follow-up, during render so the last order's
  // phase never paints next to it.
  const [prevSubmitted, setPrevSubmitted] = useState(submitted);
  if (submitted !== prevSubmitted) {
    setPrevSubmitted(submitted);
    setOrder(submitted);
    setPhase(submitted ? phaseFor(submitted) : "idle");
  }

  const externalId = order?.externalId ?? null;

  useEffect(() => {
    if (phase !== "waiting" || !externalId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const deadline = Date.now() + ORDER_POLL_TIMEOUT_MS;

    const poll = async () => {
      const result = await fetchEsimOrder(externalId);
      if (cancelled) return;

      if (result.success) {
        setOrder(result.data);
        const next = phaseFor(result.data);
        if (next !== "waiting") {
          setPhase(next);
          return;
        }
      }

      if (Date.now() > deadline) {
        setPhase("timeout");
        return;
      }

      timer = setTimeout(poll, ORDER_POLL_INTERVAL_MS);
    };

    timer = setTimeout(poll, ORDER_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [phase, externalId]);

  return { order, phase };
}
